import { ReactElement, Fragment } from 'react';

import { queries } from '@zoopla-eng/zds-components/themes';
import {
    CmsArticleCollection,
    CmsArticleCollectionProps
} from '@zoopla/cms/article-collection';
import { Banner } from '@zoopla/banner';

import {
    ArticleCollection as ArticleCollectionGQL,
    CampaignCta as CampaignCTAGQL,
    Banner as BannerGQL,
    ContentBlock
} from '../../graphql.generated';

import BackupCampaignCtaImage from '../assets/backup-campaign-cta.jpg';
import {
    CampaignCta,
    HomeValuationForm
} from '@zoopla/landing-pages/campaign-cta';
import { Document } from '@contentful/rich-text-types';
import { CmsTwoColumnSplit } from '@zoopla/cms/two-column-split';

type HomePageCampaignCTA = Omit<CampaignCTAGQL, 'title' | 'description'> & {
    ccTitle: string;
    ccDescription: string;
};

type HomePageBanner = Omit<BannerGQL, 'description'> & {
    bannerDescription?: Document;
};

const articleCollection = (props: ArticleCollectionGQL) => (
    <CmsArticleCollection
        articleCollectionData={{
            ...props,
            contentType: props.contentType as string
        }}
        carouselConfig={{
            breakpoints: [
                {
                    size: queries.M[100],
                    numCards: 3
                }
            ]
        }}
        seeMoreLink={
            props.seeMoreLink as CmsArticleCollectionProps['seeMoreLink']
        }
        horizontalCardLayout={false}
    />
);
const campaignCta = (props: HomePageCampaignCTA) => (
    <CampaignCta
        title={props.ccTitle}
        body={props.ccDescription || ''}
        image={props.image?.url || BackupCampaignCtaImage}
    >
        <HomeValuationForm eventCategory="homepage" />
    </CampaignCta>
);

const componentBanner = (banner: HomePageBanner) => (
    <Banner
        title={banner.title}
        body={banner.bannerDescription}
        imageSrc={banner.imageSrc}
        bgColor={banner.backgroundColour}
        buttons={
            banner.buttons
                ? banner.buttons.map((button) => ({
                      label: button?.buttonLabel || '',
                      url: button?.buttonUrl || '',
                      category: button?.category || 'primary',
                      eventTracking: button?.eventTracking ?? undefined
                  }))
                : undefined
        }
    />
);

function componentMap(contentBlock: ContentBlock) {
    switch (contentBlock.__typename) {
        case 'ArticleCollection':
            return articleCollection(contentBlock);
        case 'CampaignCta':
            return campaignCta({
                ccTitle: '',
                ccDescription: '',
                ...contentBlock
            });
        case 'Banner':
            return componentBanner(contentBlock);
        case 'TwoColumnSplit':
            return (
                <CmsTwoColumnSplit
                    leftColumn={contentBlock.leftColumn}
                    rightColumn={contentBlock.rightColumn}
                />
            );
    }

    return null;
}

export const contentMapper = (contentBlock: ContentBlock[]) => {
    const components: ReactElement[] = [];

    contentBlock.forEach((item, index) => {
        if (!item.__typename) {
            console.warn(`No content type found for ${JSON.stringify(item)}`);
        } else {
            const el = componentMap(item);
            if (el) {
                components.push(<Fragment key={index}>{el}</Fragment>);
            } else {
                console.warn(
                    `Content mapping does not yet support ${item.__typename}.`
                );
            }
        }
    });

    return components;
};
